// Lofi classical (docs/AUDIO_DESIGN.md §12): lofi covers of Mozart and Beethoven. Each track is one piece from
// classical-scores.js in the track's key: the felt piano's right hand plays the theme (the A sections and the break) and its
// second strain or a reharmonized restatement (the B sections) note for note, the left hand keeps the piece's own
// accompaniment figure (Alberti bass, broken chords, Für Elise's arpeggios, a Rondo's oom-pah) on the piece's chords with a
// few lofi sevenths, and jazzhop's bass and boom-bap kit play under it. The theme's pickup sounds in the bar before it.
import { expand } from '../harmony.js';
import { createPiano } from './piano-instruments.js';
import { PIECES } from './classical-scores.js';

const mod12 = n => ((n % 12) + 12) % 12;
const avg = a => a.reduce((s, x) => s + x, 0) / a.length;
const MEL = [53, 93];                        // the melody's MIDI range after the transposition

/* ---------------- voicing: a close-position chord in the left hand's register, under the tune ---------------- */
// The chord's tones (root, 3rd, 5th, 7th; a 9th chord drops its 5th) stacked upwards from a lowest note in MIDI 45–57 with
// the top ≤ 69, any inversion; a piece with a `ceil` (Moonlight: its triplets stay under the tune) moves that window with
// it: top ≤ ceil + the transposition, lowest 12–24 below. Cost Σ|Δ| (voices paired from the bottom) + 2·max(0, |Δtop| − 3)
// + .3·|mean − home| (home = top − 14: 55) + .8 when the root isn't the lowest note; the first chord aims its mean at home +
// register instead. The same chord keeps its voicing.
const TONES = { maj: [0, 4, 7], min: [0, 3, 7], dim: [0, 3, 6], '7': [0, 4, 7, 10], m7: [0, 3, 7, 10], maj7: [0, 4, 7, 11],
  maj9: [0, 4, 11, 14], m9: [0, 3, 10, 14], add9: [0, 4, 7, 14], '7sus4': [0, 5, 7, 10], m7b5: [0, 3, 6, 10], dim7: [0, 3, 6, 9],
  '69': [0, 4, 9, 14], '6': [0, 4, 7, 9], m6: [0, 3, 7, 9] };
function voiceClassical(sp, prev, chord, plan) {
  const root = mod12(plan.key.tonic + chord.root), pcs = [...new Set((TONES[chord.q] || TONES.maj).map(i => mod12(root + i)))];
  if (prev && prev.length === pcs.length && pcs.every(pc => prev.some(m => mod12(m) === pc))) return prev.slice();
  const top = plan.classical ? plan.classical.top : 69, home = top - 14;
  let best = null, bc = Infinity;
  for (let k = 0; k < pcs.length; k++) for (let lo = top - 24; lo <= top - 12; lo++) {
    if (mod12(lo) !== pcs[k]) continue;
    const v = [lo];
    for (let j = 1; j < pcs.length; j++) { let m = v[j - 1] + 1; while (mod12(m) !== pcs[(k + j) % pcs.length]) m++; v.push(m); }
    if (v[v.length - 1] > top) continue;
    let c = k ? 0.8 : 0;
    if (!prev) c += Math.abs(avg(v) - home - plan.register);
    else {
      for (let i = 0; i < Math.min(v.length, prev.length); i++) c += Math.abs(v[i] - prev[i]);
      c += 2 * Math.max(0, Math.abs(v[v.length - 1] - prev[prev.length - 1]) - 3) + 0.3 * Math.abs(avg(v) - home);
    }
    if (c < bc) { bc = c; best = v; }
  }
  return best || pcs.map(pc => top - 21 + mod12(pc - top + 21)).sort((a, b) => a - b);
}

/* ---------------- comping: the pieces' accompaniment figures, cells of [step, length, velocity, pick] ---------------- */
// Voicing indices: 0 = the lowest note, −1 = the top. Every figure strikes on steps 0 and 8, so a split bar's 2nd chord is
// heard; Moonlight's triplets fall between the steps (its track plays straight: swing 50, see plan()).
const ALBERTI = [0, 2, 4, 6, 8, 10, 12, 14].map((s, k) => [s, 4, [0.8, 0.56, 0.64, 0.56][k % 4], [[0], [-1], [1], [-1]][k % 4]]);
const TRIPLETS = Array.from({ length: 12 }, (_, k) => [+(k * 4 / 3).toFixed(4), 16 - k * 4 / 3, k % 3 ? 0.5 : 0.6, [[0], [1], [-1]][k % 3]]);
const COMPS = {
  hold: [[0, 16, 1]],                                                                                     // a held chord
  pulse: [[0, 8, 1], [8, 8, 0.8]],                                                                        // on 1 and 3
  broken: [[0, 16, 0.9, [0]], [2, 14, 0.66, [1]], [4, 12, 0.7, [-1]], [8, 8, 0.8, [0]], [10, 6, 0.64, [1, -1]]],
  alberti: ALBERTI,                                                                                       // low-high-mid-high in 8ths
  arp: [[0, 8, 0.85, [0]], [1, 7, 0.62, [1]], [2, 6, 0.66, [-1]], [8, 8, 0.8, [0]], [9, 7, 0.6, [1]], [10, 6, 0.64, [-1]]],   // Für Elise's left hand
  oompah: [[0, 4, 0.9, [0]], [2, 2, 0.55, [1, -1]], [4, 2, 0.5, [1, -1]], [6, 2, 0.52, [1, -1]],
    [8, 4, 0.85, [0]], [10, 2, 0.55, [1, -1]], [12, 2, 0.5, [1, -1]], [14, 2, 0.52, [1, -1]]],             // bass, then the chord
  throb: [[0, 4, 0.75], [4, 4, 0.55], [8, 4, 0.68], [12, 4, 0.55]],                                         // repeated chords on the beat
  murmur: [0, 2, 4, 6, 8, 10, 12, 14].map((s, k) => [s, 3, k % 2 ? 0.5 : 0.6, k % 2 ? [0] : [1]]),            // the Pathétique's inner voice
  triplets: TRIPLETS,                                                                                     // Moonlight: low, middle, top
};
const ANY = Object.fromEntries(Object.keys(COMPS).map(id => [id, [0.5, 1]]));   // drawn by planTrack; plan() sets the piece's own

/** The track's piece and how far it moves: to the key, then by octaves into the melody's range. A piece of the key's mode,
 *  drawn from the ones the session played longest ago (`played`, most recent first: left out while two others remain), so
 *  a piece never comes twice in a row and a session goes through the others first. */
export function pieceFor(key, r, played = []) {
  const all = Object.keys(PIECES).filter(id => PIECES[id].mode === key.mode);
  let ids = all;
  for (let k = played.length; k > 0; k--) { const f = all.filter(id => !played.slice(0, k).includes(id)); if (f.length >= 2) { ids = f; break; } }
  const id = ids[Math.floor(r() * ids.length)], P = PIECES[id];
  let shift = mod12(key.tonic - P.tonic);
  if (shift > 6) shift -= 12;
  if (P.hi + shift > MEL[1]) shift -= 12;
  if (P.lo + shift < MEL[0]) shift += 12;
  return { id, shift };
}
const partOf = b => (b.sec === 'B' ? 'b' : 'a');
/** The pieces a session played, most recent first (the last four of each mode), kept as one entry of plan.recent (which
 *  the queue, the radio and Mix pass to the next track; arrange.js reads only its progression ids). */
const PLAYED = 'pieces:';
const playedOf = recent => ((recent || []).find(x => x.startsWith(PLAYED)) || PLAYED).slice(PLAYED.length).split(',').filter(id => PIECES[id]);
function remember(played, id) {
  const n = { major: 0, minor: 0 };
  return [id, ...played].filter(x => n[PIECES[x].mode]++ < 4);
}
/** a tune's accents: the downbeat, then beat 3, the other beats, the offbeats */
const accent = s => (s % 16 === 0 ? 0.66 : s % 8 === 0 ? 0.62 : s % 4 === 0 ? 0.58 : 0.54);

export default {
  id: 'classical', name: 'Classical', desc: 'Lofi Mozart and Beethoven: Für Elise, Ode to Joy and more on piano',
  //            chill                        balanced                     upbeat
  energy: {
    bpm:        [[60, 68],                   [66, 78],                    [74, 86]],
    swing:      [[54, 58],                   [53, 57],                    [52, 56]],
    drumLevel:  [0.72,                       0.84,                        0.94],
    kitLp:      [[4200, 6200],               [5200, 7800],                [6200, 9000]],
    epLp:       [[2000, 3000],               [2400, 3600],                [2800, 4200]],     // the felt low-pass on the piano bus
    strum:      [[0.012, 0.03],              [0.01, 0.024],               [0.008, 0.018]],
    epVel:      [[0.4, 0.48],                [0.42, 0.5],                 [0.44, 0.53]],     // the left hand, under the tune
    comp:       [ANY,                        ANY,                         ANY],
    push:       [0,                          0,                           0],                // the harmony moves with the score
    lead:       [1,                          1,                           1],                // every track plays its tune
    leadFrom:   [1,                          1,                           1],
    breakLead:  [0.45,                       0.55,                        0.65],
    tone:       [[4200, 6400],               [5200, 8000],                [6200, 9200]],
    wowCents:   [[7, 12],                    [5, 10],                     [4, 8]],
    dust:       [[1, 1.6],                   [0.8, 1.4],                  [0.6, 1.2]],
    roll:       [[0.15, 0.3],                [0.1, 0.22],                 [0.05, 0.15]],     // odds a block chord is rolled
    // the development (§7) keeps to the score: no key lift (the tune would stay behind) and no solo; an intro is the
    // chords, the beat or a cold start, never the tune (the A brings it in on its pickup)
    lift:       [0,                          0,                           0],
    solo:       [0,                          0,                           0],
    intro:      [{ keys: 0.5, beat: 0.2, groove: 0.1, cold: 0.2 }, { keys: 0.4, beat: 0.25, groove: 0.15, cold: 0.2 },
                 { keys: 0.3, beat: 0.3, groove: 0.2, cold: 0.2 }],
  },
  qualities: { maj: [0, 4, 7], min: [0, 3, 7], dim: [0, 3, 6] },
  comps: COMPS,
  vary: false,                                           // the piece's own chords on every pass
  bridge: false,
  keysLevel: 1.7,

  plan(plan, { r, E }) {
    const played = playedOf(plan.recent), { id, shift } = pieceFor(plan.key, r, played), P = PIECES[id];
    plan.recent = [PLAYED + remember(played, id).join(','), ...plan.recent.filter(x => !x.startsWith(PLAYED))];
    plan.classical = { piece: id, shift, top: P.ceil != null ? P.ceil + shift : 69 };
    if (P.swing) plan.swing = P.swing;                   // Moonlight's triplets: straight 16ths
    if (P.groove) plan.drums.pattern = P.groove;         // and a sparse beat (P5: hats on the beat, rims on 2 and 4)
    plan.piece = { id, name: P.name, by: P.by };
    plan.progA = { id: `${id}:a`, ...expand(P.a.chords) };
    plan.progB = { id: `${id}:b`, ...expand(P.b.chords) };
    plan.chordBars = 1;
    plan.ep.comp = { ...P.comp };
    const rr = (a, b, d = 2) => +(a + r() * (b - a)).toFixed(d);
    plan.piano = { detune: rr(0.5, 1.4), stretch: rr(0.7, 1.3), hammer: rr(0.8, 1.2), bright: rr(0.9, 1.15), roll: rr(...E.roll) };
    plan.lead.timbre = 'piano';
  },

  voicing: voiceClassical,

  /** The bar of the piece's part this section plays (a: A and the break, b: B), and when the next bar starts a part, its
   *  pickup at the end of this one (the notes it overlaps are cut short), even where this bar plays no tune (the intro). */
  lead({ plan, st, i, b }) {
    const C = plan.classical;
    if (!C) return null;
    const P = PIECES[C.piece], next = st.map[i + 1];
    let notes = b.L.lead ? P[partOf(b)].bars[b.j % 8] : [];
    if (next && next.L.lead && next.j % 8 === 0) {
      const pk = P[partOf(next)].pickup;
      if (pk.length) {
        const from = 16 + pk[0].s;
        notes = [...notes.filter(n => n.s < from).map(n => ({ ...n, d: Math.min(n.d, from - n.s) })), ...pk.map(n => ({ ...n, s: 16 + n.s }))];
      }
    }
    return notes.map(n => ({ s: n.s, midi: n.m + C.shift, vel: accent(n.s), dur: n.d }));
  },

  voices(ac, dry, wet, plan, ctx) {
    const piano = createPiano(ac, dry, wet, plan, ctx);
    return { ep: piano, lead: piano.right };
  },
};
